import React from 'react';
import { StyleSheet, TouchableOpacity, TouchableOpacityProps, View } from 'react-native';

import PlayIcon from '@/assets/icons/play';
import { Text } from '@/components/TextComponent';
import { AppColors } from '@/components/lib/theme';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { ProductDocumentDataBodyLeaguesSliceItem } from '@/types/prismic.generated';

import { Box, Image, Row } from './lib/components';

const TAG_OUTSIDE_HEIGHT_SMALL = 6;
export const TAG_OUTSIDE_HEIGHT_LARGE = 10;

export const SMALL_TILE_WIDTH = 52;
export const SMALL_TILE_HEIGHT = 68;
export const LARGE_TILE_HEIGHT = 118;
export const SMALL_LOGO_CONTAINER_SIZE = 44;

const styles = StyleSheet.create({
    base: {
        width: 72,
        height: LARGE_TILE_HEIGHT,
        marginTop: TAG_OUTSIDE_HEIGHT_LARGE,
    },
    small: {
        width: SMALL_TILE_WIDTH,
        height: SMALL_TILE_HEIGHT,
        marginTop: TAG_OUTSIDE_HEIGHT_SMALL,
    },
    largeContainer: {
        width: 68,
        height: 68,
        borderRadius: 21,
    },
    smallContainer: {
        width: SMALL_LOGO_CONTAINER_SIZE,
        height: SMALL_LOGO_CONTAINER_SIZE,
        borderRadius: 14,
    },
    logoContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: designSystem.colors.gray8,
        borderCurve: 'continuous',
        borderWidth: 1.5,
        borderColor: designSystem.colors.gray6,
    },
    selectedStyle: {
        borderColor: designSystem.colors.liliac,
        backgroundColor: withOpacity(designSystem.colors.liliac, 0.2),
    },
    image: {
        width: 32,
        height: 32,
    },
    imageSmall: { width: 22, height: 22 },
    tagSmall: { padding: 3, borderRadius: 5, top: -TAG_OUTSIDE_HEIGHT_SMALL },
    tagLarge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, top: -TAG_OUTSIDE_HEIGHT_LARGE },
    tag: {
        zIndex: 10,
    },
});

type Props = {
    Icon?: JSX.Element;
    children?: React.ReactNode;
    image?: ProductDocumentDataBodyLeaguesSliceItem['banner'] | { url?: string };
    disabled?: boolean;
    size: 'small' | 'large';
    selected: boolean;
    isLive: boolean;
} & TouchableOpacityProps;

export const ButtonTitleIcon = ({ Icon, image, children, disabled, size, isLive, ...props }: Props) => {
    const opacity = disabled ? 0.3 : 1;
    const sizeStyles = size === 'small' ? styles.small : styles.base;
    const imageStyles = size === 'small' ? styles.imageSmall : styles.image;
    const containerStyle = size === 'small' ? styles.smallContainer : styles.largeContainer;
    const selectedStyle = props.selected ? styles.selectedStyle : {};

    return (
        <TouchableOpacity {...props} style={[sizeStyles, common.alignCenter, { opacity }]} disabled={disabled}>
            {isLive ? <LiveTag size={size} /> : null}
            <View
                style={[common.justifyCenter, common.alignCenter, styles.logoContainer, containerStyle, selectedStyle]}
            >
                {image?.url ? <Image source={{ uri: image.url }} style={imageStyles} resizeMode={'contain'} /> : Icon}
            </View>
            {children}
        </TouchableOpacity>
    );
};

export const LiveTag = ({ size }: { size: 'small' | 'large' }) => {
    return <Tag label={'Live'} color={'red2'} size={size} Icon={<PlayIcon />} />;
};

const Tag = ({
    Icon,
    size,
    color,
    label,
}: {
    Icon?: JSX.Element;
    size: 'small' | 'large';
    color: AppColors;
    label?: string;
}) => {
    const tagStyle = size === 'small' ? styles.tagSmall : styles.tagLarge;
    return (
        <Row
            backgroundColor={color}
            style={[tagStyle, styles.tag]}
            justifyContent={'center'}
            alignItems={'center'}
            position={'absolute'}
        >
            <Box width={10} height={10}>
                {Icon}
            </Box>
            {size === 'large' ? (
                <Text marginLeft={'s2'} variant="labelMedium" color={'gray8'}>
                    {label}
                </Text>
            ) : null}
        </Row>
    );
};
